// public/js/modules/map/static-preview.js
// A small, non-interactive map picture built from the raster tiles we already
// use — the replacement for the Google Static Maps thumbnails (docs/maps.md).
//
// WHY not Google. Their terms forbid caching or storing the returned image, so
// a map in a generated report is out; and §3.2.3(e) forbids using Google Maps
// content "with or near a non-Google Map", which is exactly how this app is
// built — MapLibre basemaps everywhere else. Dropping it removes both, plus an
// API key and a bill that only grows.
//
// WHY not a MapLibre instance. One per asset page would be fine; one per row of
// a hover list would not. This is a handful of <img> tags in a clipped box,
// which is all a static map ever was.

const TILE = 256;

const lon2x = (lon, z) => ((lon + 180) / 360) * Math.pow(2, z);
const lat2y = (lat, z) => {
  const r = lat * Math.PI / 180;
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * Math.pow(2, z);
};

/**
 * Build the HTML for a static map preview.
 *
 * @param {object}   o
 * @param {number}   o.lat
 * @param {number}   o.lon
 * @param {number}  [o.zoom=14]
 * @param {number}   o.width      CSS pixels
 * @param {number}   o.height
 * @param {function} o.tileUrl    (x, y, z) => url
 * @param {string}  [o.attribution]
 * @param {string}  [o.alt]
 * @returns {string} HTML
 */
export function staticMapHtml({ lat, lon, zoom = 14, width, height, tileUrl, attribution = '', alt = 'Map' }) {
  const z = Math.max(0, Math.min(19, Math.round(zoom)));
  const cx = lon2x(lon, z) * TILE;          // centre, in world pixels
  const cy = lat2y(lat, z) * TILE;
  const left = cx - width / 2;              // top-left of the viewport
  const top = cy - height / 2;

  // Which tiles touch the viewport, plus the fractional offset of the first.
  const x0 = Math.floor(left / TILE), y0 = Math.floor(top / TILE);
  const x1 = Math.floor((left + width) / TILE), y1 = Math.floor((top + height) / TILE);
  const offX = x0 * TILE - left, offY = y0 * TILE - top;
  const n = Math.pow(2, z);

  const imgs = [];
  for (let x = x0; x <= x1; x += 1) {
    for (let y = y0; y <= y1; y += 1) {
      if (y < 0 || y >= n) continue;                 // above the pole / below it
      const wrapped = ((x % n) + n) % n;             // wrap across the date line
      imgs.push(
        `<img src="${tileUrl(wrapped, y, z)}" alt="" aria-hidden="true" loading="lazy" draggable="false"`
        + ` style="position:absolute;width:${TILE}px;height:${TILE}px;`
        + `left:${offX + (x - x0) * TILE}px;top:${offY + (y - y0) * TILE}px;">`);
    }
  }

  // The pin sits dead centre by construction - that is what the viewport was
  // built around - so it needs no projection of its own.
  const pin = `<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true"
      style="position:absolute;left:50%;top:50%;transform:translate(-50%,-100%);
             filter:drop-shadow(0 1px 2px rgba(0,0,0,.45));">
      <path fill="#dc2626" stroke="#fff" stroke-width="1.5"
            d="M12 2c-3.9 0-7 3.1-7 7 0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"/>
      <circle cx="12" cy="9" r="2.5" fill="#fff"/>
    </svg>`;

  const credit = attribution
    ? `<span style="position:absolute;right:0;bottom:0;background:rgba(255,255,255,.75);
         color:#374151;font-size:9px;line-height:1.3;padding:1px 4px;max-width:100%;
         overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${attribution}</span>`
    : '';

  // The tiles are absolutely positioned against this box, so it must be the
  // exact size the layout maths assumed - not a stretched or letterboxed
  // version of it. aspect-ratio keeps that true at any width.
  return `<div class="static-map" role="img" aria-label="${alt}"
    style="position:relative;overflow:hidden;width:${width}px;max-width:100%;
           aspect-ratio:${width}/${height};background:#e5e7eb;">`
    + `${imgs.join('')}${pin}${credit}</div>`;
}

/**
 * The same preview as a DOM ELEMENT rather than a string.
 *
 * Use this for a Bootstrap popover. Bootstrap sanitises string content and its
 * allow-list carries neither `style` nor `<svg>`, so the string version arrives
 * with every inline style stripped and the pin gone: the tiles then flow
 * unpositioned and the box shows its top-left corner instead of the asset. An
 * element is passed through untouched.
 */
export function staticMapElement(opts) {
  const host = document.createElement('div');
  host.innerHTML = staticMapHtml(opts);
  return host.firstElementChild;
}

/** The OSM raster tiles, which carry no key and no per-request cost. */
export const osmTile = (x, y, z) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
export const OSM_CREDIT = '© OpenStreetMap';
